import React, { useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import "./StatementRecords.css";
import { useNavigate } from "react-router-dom";
import InnerHeader from "../../components/InnerHeader";

export default function TransactionRecords({ transactionData = [] }) {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState("All");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("Last 7 days");

  /* ----------------------------------
     Status mapping (API → UI)
  -----------------------------------*/
  const statusMap = {
    1: { text: "Success", type: "success" },
    0: { text: "Processing", type: "processing" },
    2: { text: "Failed", type: "failed" },
  };

  /* ----------------------------------
     Date filter helper
  -----------------------------------*/
  const isDateInRange = (date, range) => {
    const today = new Date();
    const txnDate = new Date(date);

    if (range === "Today") {
      return txnDate.toDateString() === today.toDateString();
    }

    if (range === "Yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return txnDate.toDateString() === yesterday.toDateString();
    }

    if (range === "Last 7 days") {
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);
      return txnDate >= lastWeek && txnDate <= today;
    }

    return true;
  };

  /* ----------------------------------
     Apply filters
  -----------------------------------*/
  const filteredTransactions = Array.isArray(transactionData)
  ? transactionData.filter((tx) => {
      const isStatusMatch =
        statusFilter === "All" ||
        (statusFilter === "Success" && tx.key === 1) ||
        (statusFilter === "Pending" && tx.key === 0) ||
        (statusFilter === "Fail" && tx.key === 2);

      const isPaymentTypeMatch =
        paymentTypeFilter === "All" ||
        (paymentTypeFilter === "Deposit" && tx.action === "deposit") ||
        (paymentTypeFilter === "Withdrawal" && tx.action === "withdraw");

      const isDateMatch = isDateInRange(tx.created_at, dateFilter);

      return isStatusMatch && isPaymentTypeMatch && isDateMatch;
    })
  : [];


  /* ----------------------------------
     Group by date
  -----------------------------------*/
  const groupedTransactions = filteredTransactions.reduce((acc, tx) => {
    const date = new Date(tx.created_at).toDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(tx);
    return acc;
  }, {});

  return (
    <div className="records-container">
      <InnerHeader title="Statement" />

      {/* Date Filter */}
      <div className="filter-section">
        <div className="date-filter">
          <span className="filter-text">{dateFilter}</span>
          <button className="filter-icon-button">
            <SlidersHorizontal size={20} />
          </button>
        </div>
      </div>

      {/* Transaction List */}
      <div className="records-list">
        {Object.keys(groupedTransactions).length === 0 && (
          <div className="empty">No records</div>
        )}

        {Object.entries(groupedTransactions).map(([date, transactions]) => (
          <div key={date}>
            {transactions.map((tx) => {
              const status = statusMap[tx.key] || {
                text: "Unknown",
                type: "processing",
              };

              return (
                <TransactionCard
                  key={tx.id}
                  date={date}
                  status={status.text}
                  statusType={status.type}
                  type={tx.action === "deposit" ? "Deposit" : "Withdrawal"}
                  transactionId={tx.transaction_id}
                  method={tx.payment_method || "—"}
                  time={new Date(tx.created_at).toLocaleString()}
                  amount={`৳ ${Number(tx.amount).toFixed(2)}`}
                  isPositive={tx.action === "deposit"}
                  transaction={tx} // ✅ FULL TX PASSED
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="records-footer">
        <span className="footer-text">
          {filteredTransactions.length} records
        </span>
      </div>
    </div>
  );
}

/* ----------------------------------
   Transaction Card
-----------------------------------*/
function TransactionCard({
  date,
  status,
  statusType,
  type,
  transactionId,
  method,
  time,
  amount,
  isPositive,
  transaction,
}) {
  const navigate = useNavigate();

  const goToDetails = () => {
    navigate("/transaction-detail", {
      state: { transaction },
    });
  };

  return (
    <div className="transaction-group" onClick={goToDetails}>
      {/* Date */}
      <div className="card-date">{date}</div>

      {/* Card */}
      <div className="card-content">
        <div className={`status-badge ${statusType}`}>
          <span className="status-text">{status}</span>
        </div>

        <div className="transaction-type">{type}</div>

        <div className="details-row">
          <div className="transaction-info">
            <div className="transaction-id">{transactionId}</div>
            <div className="transaction-method">{method}</div>
          </div>

          <button
            className="details-btn"
            onClick={(e) => {
              e.stopPropagation(); // ✅ prevent card click duplication
              goToDetails();
            }}
          >
            Details
          </button>
        </div>

        <div className="bottom-row">
          <div className="transaction-time">{time}</div>
          <div className={`amount ${isPositive ? "positive" : "negative"}`}>
            {amount}
          </div>
        </div>
      </div>
    </div>
  );
}